import React from 'react';

import { gql, useLazyQuery } from '@apollo/client';
import { map, toString } from 'lodash';

type ChainPermissions = {
  profileEdit: boolean;
  restaurantCreate: boolean;
  periodJobCreate: boolean;
  userCreate: boolean;
};

type RestaurantPermissions = {
  jobCreate: boolean;
};

interface ChainPermissionData {
  permissions: {
    chain: ChainPermissions;
  };
}

interface RestaurantPermissionData {
  permissions: {
    restaurant: RestaurantPermissions;
  };
}

interface PermissionData {
  permissions: {
    chain: ChainPermissions;
    restaurant: RestaurantPermissions;
  };
}

const getChainPermissionGql = gql(`
  query getChainPermission($chainId: ID!) {
    permissions {
      chain(chainId: $chainId) {
        profileEdit
        restaurantCreate
        periodJobCreate
        userCreate: createUser
      }
    }
  }
`);

const getRestaurantPermissionGql = gql(`
  query getRestaurantPermission($restaurantId: ID!) {
    permissions {
      restaurant(restaurantId: $restaurantId) {
        jobCreate
      }
    }
  }
`);

const getUserPermissionGql = gql(`
  query getPermission($chainId: ID!, $restaurantId: ID!) {
    permissions {
      chain(chainId: $chainId) {
        profileEdit
        restaurantCreate
        periodJobCreate
        userCreate: createUser
      }
      restaurant(restaurantId: $restaurantId) {
        jobCreate
      }
    }
  }
`);

// New source — single query loads every restaurant's permission names once, so per-restaurant switching is instant (no refetch).
const getUserChainRestaurantsPermissionsGql = gql(`
  query getUserChainRestaurantsPermissions {
    user {
      chain {
        restaurants {
          id
          permissions {
            id
            name
          }
        }
      }
    }
  }
`);

interface ChainRestaurantsPermissionData {
  user: {
    chain: {
      restaurants: Array<{
        id: string;
        permissions: Array<{ id: string; name: string }>;
      }>;
    } | null;
  };
}

const useGetPermissions = () => {
  const [getChainPermissionQuery] = useLazyQuery<ChainPermissionData>(getChainPermissionGql, {
    fetchPolicy: 'network-only',
  });

  const [getRestaurantPermissionQuery] = useLazyQuery<RestaurantPermissionData>(getRestaurantPermissionGql, {
    fetchPolicy: 'network-only',
  });

  const [getUserPermissionQuery] = useLazyQuery<PermissionData>(getUserPermissionGql, {
    fetchPolicy: 'network-only',
  });

  const [getUserChainRestaurantsPermissionsQuery] = useLazyQuery<ChainRestaurantsPermissionData>(
    getUserChainRestaurantsPermissionsGql,
    { fetchPolicy: 'network-only' },
  );

  const getChainPermissions = React.useCallback(
    async (chainId: string) => {
      const { data } = await getChainPermissionQuery({ variables: { chainId: toString(chainId) } });
      return data?.permissions;
    },
    [getChainPermissionQuery],
  );

  const getRestaurantPermissions = React.useCallback(
    async (restaurantId: string) => {
      const { data } = await getRestaurantPermissionQuery({ variables: { restaurantId: toString(restaurantId) } });
      return data?.permissions;
    },
    [getRestaurantPermissionQuery],
  );

  const getUserPermissions = React.useCallback(
    async (chainId: string, restaurantId: string) => {
      const { data } = await getUserPermissionQuery({
        variables: { chainId: toString(chainId), restaurantId: toString(restaurantId) },
      });
      return data?.permissions;
    },
    [getUserPermissionQuery],
  );

  const getUserChainRestaurantsPermissions = React.useCallback(async () => {
    const { data } = await getUserChainRestaurantsPermissionsQuery();
    const restaurants = data?.user?.chain?.restaurants ?? [];

    return restaurants.reduce<Record<string, string[]>>(
      (prev, { id, permissions: perms }) => ({ ...prev, [id]: map(perms, 'name') }),
      {},
    );
  }, [getUserChainRestaurantsPermissionsQuery]);

  return { getChainPermissions, getRestaurantPermissions, getUserPermissions, getUserChainRestaurantsPermissions };
};

export default useGetPermissions;
